import { Checkbox } from "@/components/ui/checkbox"
import {
  REGION_BORDER_COLORS,
  RegionBorderWidth,
  useRegionBorders,
} from "@/components/maps/regionBorders"
import { cn } from "@/lib/utils"
import { useTranslation } from "react-i18next"

const WIDTH_OPTIONS = [
  { value: "thin", label: "maps.regionBorders.widths.thin" },
  { value: "medium", label: "maps.regionBorders.widths.medium" },
  { value: "thick", label: "maps.regionBorders.widths.thick" },
] as const satisfies readonly { value: RegionBorderWidth; label: string }[]

// Color names are labels only; the stored setting is the hex value.
const COLOR_NAME_KEYS = {
  Graphite: "maps.regionBorders.colors.graphite",
  Purple: "maps.regionBorders.colors.purple",
  Red: "maps.regionBorders.colors.red",
  Blue: "maps.regionBorders.colors.blue",
  Orange: "maps.regionBorders.colors.orange",
} as const

const RegionBordersSettings = () => {
  const { t } = useTranslation()
  const [settings, update] = useRegionBorders()

  return (
    <div className="border-t p-2 text-sm" data-testid="region-borders-settings">
      <label className="flex cursor-pointer items-center gap-2 px-1 py-1">
        <Checkbox
          id="region-borders-enabled"
          checked={settings.enabled}
          onCheckedChange={(checked) => update({ enabled: checked === true })}
        />
        {t("maps.regionBorders.title")}
      </label>
      {settings.enabled && (
        <div className="mt-1 flex flex-col gap-2 px-1">
          <div
            className="flex gap-1.5"
            role="radiogroup"
            aria-label={t("maps.regionBorders.color")}
          >
            {REGION_BORDER_COLORS.map((color) => (
              <button
                key={color.value}
                type="button"
                role="radio"
                aria-checked={settings.color === color.value}
                title={
                  color.name in COLOR_NAME_KEYS
                    ? t(
                        COLOR_NAME_KEYS[
                          color.name as keyof typeof COLOR_NAME_KEYS
                        ],
                      )
                    : color.name
                }
                onClick={() => update({ color: color.value })}
                className={cn(
                  "h-5 w-5 rounded-full border-2",
                  settings.color === color.value
                    ? "border-foreground"
                    : "border-transparent",
                )}
                style={{ backgroundColor: color.value }}
              />
            ))}
          </div>
          <div className="flex overflow-hidden rounded-md border">
            {WIDTH_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => update({ width: option.value })}
                className={cn(
                  "flex-1 px-1 py-0.5 text-xs",
                  settings.width === option.value
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50",
                )}
              >
                {t(option.label)}
              </button>
            ))}
          </div>
          <label className="flex cursor-pointer items-center gap-2 py-1">
            <Checkbox
              id="region-borders-labels"
              checked={settings.showLabels}
              onCheckedChange={(checked) =>
                update({ showLabels: checked === true })
              }
            />
            {t("maps.regionBorders.names")}
          </label>
        </div>
      )}
    </div>
  )
}

export default RegionBordersSettings
